import React, { createContext, useContext, useState, useCallback } from 'react';
import { useAuthenticator } from '@aws-amplify/ui-react';
import LoginPromptModal from '../components/LoginPromptModal';

type PromptAction = 'play' | 'queue' | 'download' | 'favorite' | 'shownotes';

interface AuthPromptContextType {
  isAuthenticated: boolean;
  user: any;
  promptLogin: (action?: PromptAction, streamName?: string) => void;
  requireAuth: (action: PromptAction, streamName?: string) => boolean;
}

const AuthPromptContext = createContext<AuthPromptContextType | null>(null);

export const AuthPromptProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuthenticator((context) => [context.user]);
  const [isPromptOpen, setIsPromptOpen] = useState(false);
  const [promptAction, setPromptAction] = useState<PromptAction>('play');
  const [promptStreamName, setPromptStreamName] = useState<string | undefined>();

  const isAuthenticated = !!user;

  const promptLogin = useCallback((action: PromptAction = 'play', streamName?: string) => {
    setPromptAction(action);
    setPromptStreamName(streamName);
    setIsPromptOpen(true);
  }, []);

  const closePrompt = useCallback(() => {
    setIsPromptOpen(false);
    setPromptStreamName(undefined);
  }, []);

  // Returns true if authenticated, false if not (and shows prompt)
  const requireAuth = useCallback((action: PromptAction, streamName?: string): boolean => {
    if (isAuthenticated) {
      return true;
    }
    promptLogin(action, streamName);
    return false;
  }, [isAuthenticated, promptLogin]);

  const value: AuthPromptContextType = {
    isAuthenticated,
    user,
    promptLogin,
    requireAuth,
  };

  return (
    <AuthPromptContext.Provider value={value}>
      {children}
      <LoginPromptModal
        isOpen={isPromptOpen}
        onClose={closePrompt}
        action={promptAction}
        streamName={promptStreamName}
      />
    </AuthPromptContext.Provider>
  );
};

export const useAuthPrompt = (): AuthPromptContextType => {
  const context = useContext(AuthPromptContext);
  if (!context) {
    throw new Error('useAuthPrompt must be used within AuthPromptProvider');
  }
  return context;
};
